// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// Feature 089 — one process, one document. The switcher lives in the
// Event Storming header: it lists every live process (name, step count,
// state), lets the consultant create one by name (no model turn), rename
// it inline, reorder it, and remove it (moving it to a "Removed" section
// it can be restored from). It never re-sorts what it is given — the
// order the caller passes is the order rendered.

import { useState } from 'react';
import { stateLabel } from '../utils/processLandscape';
import '../styles/ProcessSwitcher.css';

export interface ProcessSwitcherEntry {
  processId: string;
  name: string;
  stepCount: number;
  state: 'empty' | 'narrated' | 'walked' | 'agreed';
  removedAt: string | null;
  // 095: the business map's storm order and importance. Optional so every
  // caller that predates the business map keeps working unchanged.
  importance?: 'core' | 'supporting' | 'generic' | null;
  rank?: number | null;
}

interface ProcessSwitcherProps {
  processes: ProcessSwitcherEntry[];
  focusedProcessId: string | null;
  onSelect: (processId: string) => void;
  onCreate: (name: string) => void;
  onRename: (processId: string, name: string) => void;
  onRemove: (processId: string) => void;
  onRestore: (processId: string) => void;
  onReorder: (processId: string, direction: 'up' | 'down') => void;
  // 093: the landscape — every process on one map. Present when the host
  // screen can show it; `landscapeActive` marks it as the current view.
  onLandscape?: () => void;
  landscapeActive?: boolean;
}

export default function ProcessSwitcher({
  processes, focusedProcessId, onSelect, onCreate, onRename, onRemove, onRestore, onReorder, onLandscape, landscapeActive = false,
}: ProcessSwitcherProps) {
  // 095: storm order. Live entries sort by rank ascending, unranked ones
  // last, and ties (including "no rank anywhere") keep the order given —
  // the switcher never re-sorts beyond that.
  const live = processes
    .map((process, index) => ({ process, index }))
    .filter(({ process }) => !process.removedAt)
    .sort((a, b) => {
      const rankA = a.process.rank ?? null;
      const rankB = b.process.rank ?? null;
      if (rankA != null && rankB != null) return rankA - rankB;
      if (rankA != null) return -1;
      if (rankB != null) return 1;
      return a.index - b.index;
    })
    .map(({ process }) => process);
  const removed = processes.filter((process) => process.removedAt);
  // "Storm next": the first entry in the storm order with no steps yet.
  const stormNextId = live.find((process) => process.stepCount === 0)?.processId ?? null;

  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');

  const startCreate = () => { setCreating(true); setCreateName(''); };
  const submitCreate = () => {
    const name = createName.trim();
    setCreating(false);
    if (name) onCreate(name);
  };

  const startRename = (processId: string, currentName: string) => {
    setRenamingId(processId);
    setRenameName(currentName);
  };
  const submitRename = () => {
    const processId = renamingId;
    const name = renameName.trim();
    setRenamingId(null);
    if (processId && name) onRename(processId, name);
  };

  const stepLabel = (count: number) => `${count} step${count === 1 ? '' : 's'}`;

  return (
    <div className="process-switcher">
      {onLandscape ? (
        <button
          type="button"
          className={`process-switcher-landscape${landscapeActive ? ' is-active' : ''}`}
          aria-pressed={landscapeActive}
          onClick={onLandscape}
        >
          <span className="process-switcher-landscape-name">All processes</span>
          <span className="process-switcher-meta">the map · {live.length} process{live.length === 1 ? '' : 'es'}</span>
        </button>
      ) : null}
      <ul className="process-switcher-list">
        {live.map((process, index) => {
          const focused = process.processId === focusedProcessId;
          return (
            <li
              key={process.processId}
              className={`process-switcher-item${focused ? ' process-switcher-item--focused' : ''}`}
              aria-current={focused ? 'true' : undefined}
            >
              {renamingId === process.processId ? (
                <span className="process-switcher-rename">
                  <input
                    className="process-switcher-input"
                    aria-label={`Rename ${process.name}`}
                    autoFocus
                    value={renameName}
                    onChange={(e) => setRenameName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitRename();
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                  />
                  <button type="button" className="process-switcher-btn" onClick={submitRename}>Save</button>
                  <button type="button" className="process-switcher-btn" onClick={() => setRenamingId(null)}>Cancel</button>
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="process-switcher-name"
                    onClick={() => onSelect(process.processId)}
                  >
                    {process.name}
                  </button>
                  {process.importance ? (
                    <span className={`process-switcher-importance process-switcher-importance--${process.importance}`}>
                      {process.importance}
                    </span>
                  ) : null}
                  {process.processId === stormNextId ? (
                    <span className="process-switcher-storm-next">Storm next</span>
                  ) : null}
                  <span className="process-switcher-meta">
                    {stepLabel(process.stepCount)} · {stateLabel(process.state)}
                  </span>
                  <span className="process-switcher-actions">
                    <button
                      type="button"
                      className="process-switcher-btn"
                      aria-label={`Move ${process.name} up`}
                      disabled={index === 0}
                      onClick={() => onReorder(process.processId, 'up')}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="process-switcher-btn"
                      aria-label={`Move ${process.name} down`}
                      disabled={index === live.length - 1}
                      onClick={() => onReorder(process.processId, 'down')}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="process-switcher-btn"
                      aria-label={`Rename ${process.name}`}
                      onClick={() => startRename(process.processId, process.name)}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="process-switcher-btn"
                      aria-label={`Remove ${process.name}`}
                      onClick={() => onRemove(process.processId)}
                    >
                      Remove
                    </button>
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {creating ? (
        <span className="process-switcher-create">
          <input
            className="process-switcher-input"
            aria-label="Process name"
            autoFocus
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitCreate();
              if (e.key === 'Escape') setCreating(false);
            }}
          />
          <button type="button" className="process-switcher-btn process-switcher-btn--primary" onClick={submitCreate}>
            Create
          </button>
          <button type="button" className="process-switcher-btn" onClick={() => setCreating(false)}>
            Cancel
          </button>
        </span>
      ) : (
        <button type="button" className="process-switcher-new" onClick={startCreate}>
          New process
        </button>
      )}

      {removed.length > 0 && (
        <div className="process-switcher-removed">
          <h3 className="process-switcher-removed-title">Removed</h3>
          <ul className="process-switcher-list">
            {removed.map((process) => (
              <li key={process.processId} className="process-switcher-item process-switcher-item--removed">
                <span className="process-switcher-name process-switcher-name--removed">{process.name}</span>
                <span className="process-switcher-meta">
                  {stepLabel(process.stepCount)} · {process.state}
                </span>
                <button
                  type="button"
                  className="process-switcher-btn"
                  aria-label={`Restore ${process.name}`}
                  onClick={() => onRestore(process.processId)}
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
